import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Avatar } from '@/deploy/components/ui/Avatar';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { ConsoleModal } from '@/deploy/components/ui/ConsoleModal';
import { useSessionStore } from '@/deploy/stores/session.store';
import { devEnvService, type DevEnv } from '@/deploy/services/dev-env.service';
import { serverControlService } from '@/deploy/services/server-control.service';
import { betaSlotService } from '@/deploy/services/beta-slot.service';
import { PushToBetaModal } from './Environments';
import { formatRelative } from '@/deploy/utils/format';
import styles from './DevEnvDetail.module.scss';

const STATUS: Record<DevEnv['status'], { label: string; tone: 'muted' | 'info' | 'success' | 'danger' }> = {
  creating:   { label: 'Création…',      tone: 'info' },
  installing: { label: 'Install Ptero…', tone: 'info' },
  running:    { label: 'En marche',      tone: 'success' },
  stopped:    { label: 'Arrêté',         tone: 'muted' },
  errored:    { label: 'Erreur',         tone: 'danger' },
};

export function DevEnvDetail() {
  const { envId } = useParams<{ envId: string }>();
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());
  const user = useSessionStore((s) => s.user);

  const [env, setEnv] = useState<DevEnv | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [stats, setStats] = useState<{ ramMb: number; cpuPct: number; uptimeSec: number } | null>(null);

  // On charge le détail via la LISTE + find(id) : le GET backend ne renvoie pas encore
  // publicAddress (contrairement à la liste), donc l'IP:port n'apparaîtrait pas sinon.
  const refetchEnv = useCallback(
    () => devEnvService.list(project.id).then((l) => l.find((e) => e.id === envId) ?? null),
    [project.id, envId],
  );

  // Chargement initial (vraies données via la liste).
  useEffect(() => {
    if (!project.id || !envId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    refetchEnv()
      .then((e) => { if (!cancelled) setEnv(e); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Erreur'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refetchEnv, project.id, envId]);

  // Poll doux tant que l'env se provisionne (creating/installing).
  useEffect(() => {
    if (!env || (env.status !== 'creating' && env.status !== 'installing')) return;
    const t = setInterval(() => {
      refetchEnv().then(setEnv).catch(() => { /* noop */ });
    }, 4000);
    return () => clearInterval(t);
  }, [env, refetchEnv]);

  // Poll des ressources (RAM / CPU / uptime) quand le serveur Ptero est provisionné.
  const statsReady = !!env && (env.status === 'running' || env.status === 'stopped') && !!env.pteroIdentifier;
  useEffect(() => {
    if (!statsReady || !project.id || !envId) { setStats(null); return; }
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const s = await serverControlService.state(project.id, 'dev', envId);
        if (!alive) return;
        setStats({
          ramMb: Math.round((s.memoryBytes ?? 0) / (1024 * 1024)),
          cpuPct: Math.round((s.cpuAbsolute ?? 0) * 10) / 10,
          uptimeSec: Math.round((s.uptimeMs ?? 0) / 1000),
        });
      } catch { /* garde l'ancienne valeur sur erreur transitoire */ }
      finally { if (alive) timer = setTimeout(tick, 3000); }
    };
    void tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [statsReady, project.id, envId]);

  if (loading) {
    return <div className={styles.page}><div className={styles.empty}>Chargement de l'environnement…</div></div>;
  }
  if (error || !env) {
    return (
      <div className={styles.notFound}>
        <h2>Env introuvable</h2>
        {error && <p style={{ color: 'var(--color-text-muted)' }}>{error}</p>}
        <Button variant="primary" onClick={() => navigate('/deploiement/environments/dev')}>Retour aux envs de dev</Button>
      </div>
    );
  }

  const st = STATUS[env.status] ?? { label: env.status, tone: 'muted' as const };
  const canManage = env.ownerId === user.id || user.role === 'admin';
  const ready = (env.status === 'running' || env.status === 'stopped') && !!env.pteroIdentifier;
  const pending = env.status === 'creating' || env.status === 'installing';

  // publicAddress = "ip:port" -> séparé en deux blocs copiables (IP puis port).
  const addr = env.publicAddress ?? '';
  const addrSep = addr.lastIndexOf(':');
  const ip = addrSep > 0 ? addr.slice(0, addrSep) : addr;
  const port = addrSep > 0 ? addr.slice(addrSep + 1) : '';

  const remove = async () => {
    if (!confirm(`Supprimer définitivement l'env "${env.displayName}" ? (Ptero + Mongo)`)) return;
    setDeleting(true);
    try {
      await devEnvService.remove(project.id, env.id);
      navigate('/deploiement/environments/dev');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur suppression');
      setDeleting(false);
    }
  };

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate('/deploiement/environments/dev')}>
        <Icon name="chevron-right" size={14} style={{ transform: 'rotate(180deg)' }} /> Tous les envs de dev
      </button>

      <PageHeader
        eyebrow={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <EnvBadge env="dev" size="sm" />
            <Avatar name={env.ownerName} color={env.ownerAvatarColor ?? '#888'} size={18} />
            {env.ownerName}{env.createdAt && <> · créé {formatRelative(env.createdAt)}</>}
          </span>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className={styles.chip} style={{ background: '#5B8DEF' }}>
              <Icon name="package" size={20} />
            </span>
            {env.displayName}
            <Badge tone={st.tone} dot>{st.label}</Badge>
          </span>
        }
        subtitle={`Base ${env.containerCode}${env.pteroIdentifier ? ` · Ptero ${env.pteroIdentifier}` : ''}`}
        actions={
          canManage ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {env.pteroServerId && (
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={<Icon name="arrow-right" size={14} />}
                  disabled={pending || pushing}
                  loading={pushing}
                  title={pending ? 'Environnement en cours de provisioning' : undefined}
                  onClick={() => setPushOpen(true)}
                >
                  Pousser en Bêta
                </Button>
              )}
              <Button variant="ghost" size="md" leftIcon={<Icon name="x" size={14} />} loading={deleting} onClick={() => void remove()}>
                Supprimer l'env
              </Button>
            </div>
          ) : undefined
        }
      />

      {/* Infos rapides */}
      <section className={styles.metaRow}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Image Docker</span>
          <code className={styles.metaValue} title={env.dockerImage}>{env.dockerImage || '—'}</code>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Base</span>
          <code className={styles.metaValue}>{env.containerCode}</code>
        </div>
        <CopyField label="Adresse IP" value={env.publicAddress ? ip : null} empty="non alloué" />
        <CopyField label="Port" value={port || null} />
        {stats && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Ressources</span>
            <div className={styles.resourceRow}>
              <div className={styles.resourceMetric}>
                <span className={styles.resourceLabel}>Uptime</span>
                <span className={styles.resourceValue}>{stats.uptimeSec > 0 ? fmtUptime(stats.uptimeSec) : '—'}</span>
              </div>
              <div className={styles.resourceMetric}>
                <span className={styles.resourceLabel}>RAM</span>
                <span className={styles.resourceValue}>{stats.ramMb} MB</span>
              </div>
              <div className={styles.resourceMetric}>
                <span className={styles.resourceLabel}>CPU</span>
                <span className={styles.resourceValue}>{stats.cpuPct}%</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {env.errorMessage && (
        <div className={styles.errorBanner}>
          <Icon name="warn" size={14} /> {env.errorMessage}
        </div>
      )}

      {/* Console live + explorateur de fichiers (drag & drop) — panneau intégré */}
      {ready ? (
        <ConsoleModal
          embedded
          open
          onClose={() => { /* embedded : pas de fermeture */ }}
          projectId={project.id}
          scope="dev"
          targetKey={env.id}
          sourceName={env.displayName}
          sourceSubtitle={`dev · ${env.containerCode}`}
          icon="server"
          crashTarget={env.pteroIdentifier ? { env: 'dev', identifier: env.pteroIdentifier } : undefined}
        />
      ) : (
        <div className={styles.empty}>
          {env.status === 'creating' || env.status === 'installing'
            ? "Environnement en cours de provisioning… la console et les fichiers seront disponibles dès que le serveur est prêt."
            : "Console et fichiers indisponibles (serveur Ptero non provisionné)."}
        </div>
      )}

      {pushOpen && (
        <PushToBetaModal
          projectId={project.id}
          env={env}
          onClose={() => setPushOpen(false)}
          onLaunch={(slot) => {
            setPushOpen(false);
            setPushing(true);
            betaSlotService.promote(project.id, slot, env.id)
              .then(() => refetchEnv().then(setEnv).catch(() => { /* refetch KO : l'op a réussi */ }))
              .catch((e) => alert(`Pousser vers Bêta ${slot} a échoué : ${e instanceof Error ? e.message : String(e)}`))
              .finally(() => setPushing(false));
          }}
        />
      )}
    </div>
  );
}

export function CopyField({ label, value, empty }: { label: string; value: string | null; empty?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const copy = () => {
    if (!value) return;
    void navigator.clipboard.writeText(value);
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={styles.metaItem}>
      <span className={styles.metaLabel}>{label}</span>
      {value ? (
        <div className={styles.copyField}>
          <code className={styles.metaValue}>{value}</code>
          <button type="button" className={styles.copyFieldBtn} onClick={copy} title={`Copier ${label}`}>
            {copied ? <><Icon name="check" size={11} /> Copié</> : 'Copier'}
          </button>
        </div>
      ) : (
        <code className={styles.metaValue} style={{ opacity: 0.55 }}>{empty ?? '—'}</code>
      )}
    </div>
  );
}

export function fmtUptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}j${Math.floor((sec % 86400) / 3600)}h`;
}
